import { QRCodeCanvas } from "qrcode.react";
import { useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { handleAddToCalendar } from "./modules/handleAddToCalendar";
import { handleDownloadPDF } from "./modules/handleDownloadPDF";
import { useConfirmation } from "./modules/useConfirmation";
import { useHotelRulesModal } from "./modules/useHotelRulesModal";
import { AddonServiceDetailsTable } from "./molecules/addonServiceDetailsTable";
import { Bookingetails } from "./molecules/bookingDetails";
import { GuestDetails } from "./molecules/guestDetails";
import { HotelRulesModal } from "./molecules/hotelRulesModal";
import { RoomDetails } from "./molecules/roomDetails";
import { HotelDetails } from "./molecules/hotelDetails";
import "../../../styles/Confirmation.css";

const ConfirmationTemplate = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const confirmationRef = useRef(null);
  const { booking, hotelInfo, branding, extraBedPrice, hotelRules } =
    useConfirmation(location, navigate);
  const { isRulesModalVisible, showHotelRulesModal, hideHotelRulesModal } =
    useHotelRulesModal();
  const { childrenAgeRule } = location.state || {};
  if (!booking || !hotelInfo || !branding || extraBedPrice === null) {
    return <p>Loading booking details... Please wait.</p>;
  }

  return (
    <div
      ref={confirmationRef}
      className="padding-10"
      style={{
        fontFamily: "Arial, sans-serif",
        backgroundColor: branding.pageBackgroundImage
          ? `url(${branding.pageBackgroundImage})`
          : "#f8f4f4",
        backgroundSize: "cover",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          maxWidth: "95%",
          margin: "auto",
          backgroundColor: branding.navbarColor,
          borderRadius: "10px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <div
          style={{
            backgroundColor: branding.headingColor,
            color: "#fff",
            fontFamily: branding.headingFont,
          }}
        >
          <h1 style={{ marginLeft: "10px" }}>
            {t("booking.confirmation.title")}
          </h1>
        </div>
        <div style={{ padding: "20px" }}>
          <HotelDetails hotelInfo={hotelInfo} branding={branding} />
          <Bookingetails booking={booking} branding={branding} />
          <h3 className="mt-3">{t("guest_information.title")}</h3>
          <GuestDetails booking={booking} branding={branding} />

          <h3 className="mt-3">
            {t("booking.confirmation.room_detail.title")}
          </h3>
          <RoomDetails
            booking={booking}
            branding={branding}
            extraBedPrice={extraBedPrice}
          />

          {/* Children Age Policy & Extra Bed Price */}
          <div className="mt-3">
            <h3>{t("booking.children_age_policy.title")}</h3>
            <p style={{ marginBottom: 0 }}>
              {t("booking.children_age_policy.description")}
            </p>
            <p style={{ marginBottom: 0 }}>
              <strong>
                {t("booking.children_age_policy.extra_bed_price")}:
              </strong>{" "}
              {childrenAgeRule?.extraPrice?.toLocaleString() || 0} VND / night
            </p>
          </div>

          <h3 className="mt-3">{t("room.add_on_services")}</h3>
          <AddonServiceDetailsTable booking={booking} branding={branding} />

          <div style={{ marginTop: "20px", textAlign: "center" }}>
            <div className="justify-content-between">
              <div
                style={{ flex: "1", textAlign: "center", marginRight: "10px" }}
              >
                <QRCodeCanvas
                  value={`${window.location.origin}/mybooking`}
                  size={120}
                  style={{ marginBottom: "10px" }}
                />
                <p style={{ textAlign: "center" }}>
                  {t("booking.confirmation.scan_booking")}
                </p>
              </div>
              <div style={{ flex: "1", textAlign: "center", margin: "0 10px" }}>
                <button
                  onClick={showHotelRulesModal}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    color: branding.roomNameColor,
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontSize: "16px",
                    display: "block",
                    margin: "10px 0",
                  }}
                >
                  {t("booking.confirmation.read_rule_hotel")}
                </button>
                <button
                  onClick={() =>
                    window.open(
                      hotelInfo.mapLocation,
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    color: "#007bff",
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontSize: "16px",
                    display: "block",
                    margin: "10px 0",
                  }}
                >
                  {t("booking.confirmation.view_on_map")}
                </button>
              </div>
              <div
                style={{ flex: "1", textAlign: "center", marginLeft: "10px" }}
              >
                <button
                  onClick={handleDownloadPDF(
                    booking.bookingId,
                    confirmationRef
                  )}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    color: branding.roomNameColor,
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontSize: "16px",
                    display: "block",
                    margin: "10px 0",
                  }}
                >
                  {t("booking.confirmation.download_booking")}
                </button>
                <button
                  onClick={handleAddToCalendar((booking, hotelInfo))}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    color: branding.roomNameColor,
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontSize: "16px",
                    display: "block",
                    margin: "10px 0",
                  }}
                >
                  {t("booking.confirmation.add_to_calendar")}
                </button>
              </div>
            </div>

            <HotelRulesModal
              isRulesModalVisible={isRulesModalVisible}
              hideHotelRulesModal={hideHotelRulesModal}
              hotelRules={hotelRules}
            />
          </div>

          <p
            style={{
              textAlign: "center",
              marginTop: "20px",
              fontSize: "18px",
              fontWeight: "bold",
              color: branding.roomNameColor,
            }}
          >
            {t("booking.confirmation.thank_you")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationTemplate;
